import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import { studentApi } from '@/api';
import {
  Alert,
  Badge,
  Button,
  Icon,
  Card,
  CardHeader,
  ConfirmDialog,
  EmptyState,
  Input,
  useToast,
} from '@/components/ui';
import { CertificatePanel } from '@/features/verification/CertificatePanel';
import {
  CERTIFICATE_ACCEPT,
  CERTIFICATE_HINT,
  CERTIFICATE_MAX_SIZE,
  DOCUMENT_TONES,
  STUDENT_CERTIFICATE_LABELS,
} from '@/features/verification/status';
import { errorMessage } from '@/hooks/useApiError';
import { formatDate, formatFileSize } from '@/utils/format';

/**
 * The fields the form edits. `credential_url` is deliberately absent: a link
 * the student types proves nothing (it can point anywhere), so it is no
 * longer asked for or shown. Leaving it out of the update keeps whatever an
 * older version stored.
 */
const BLANK = {
  name: '',
  issuer: '',
  issue_date: '',
  expiry_date: '',
  credential_id: '',
};

/** The API returns nulls; date inputs and text inputs both want strings. */
function toForm(certificate) {
  return Object.fromEntries(Object.keys(BLANK).map((key) => [key, certificate[key] ?? '']));
}

/**
 * Certificates, beside skills because they answer the same question.
 *
 * A skill is a claim the student makes; a certificate is meant to be the part
 * somebody else vouches for. So the certificate itself is uploaded and an
 * admin checks it against the details typed here -- uploading is the request,
 * as with a diploma. Only a checked file earns the Verified badge companies
 * and the public portfolio see.
 */
export function CertificationsCard() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const fileInput = useRef(null);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [file, setFile] = useState(null);
  /** The row being edited, so the form it turns into is brought into view. */
  const editRow = useRef(null);

  useEffect(() => {
    if (editingId) editRow.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [editingId]);

  const { data: certifications = [], isPending } = useQuery({
    queryKey: ['student-certifications'],
    queryFn: studentApi.listCertifications,
  });

  const editing = certifications.find((row) => row.id === editingId) ?? null;

  function refresh() {
    void queryClient.invalidateQueries({ queryKey: ['student-certifications'] });
    void queryClient.invalidateQueries({ queryKey: ['profile-completion'] });
  }

  function close() {
    setOpen(false);
    setEditingId(null);
    setForm(BLANK);
    setFile(null);
  }

  const save = useMutation({
    mutationFn: async ({ input, attachment }) => {
      const saved = editingId
        ? await studentApi.updateCertification(editingId, input)
        : await studentApi.addCertification(input);
      // The file goes up once the certificate exists, so a new one can be
      // added and sent for review in one step.
      if (attachment) {
        try {
          await studentApi.uploadCertificationFile(saved.id, attachment);
        } catch (error) {
          return { uploadError: error };
        }
      }
      return { uploaded: Boolean(attachment) };
    },
    onSuccess: ({ uploaded, uploadError }) => {
      if (uploadError) {
        toast.error(
          errorMessage(
            uploadError,
            'The certificate was saved, but the file could not be uploaded',
          ),
        );
      } else if (uploaded) {
        toast.success('Certificate saved. An admin will check the file.');
      } else {
        toast.success(editingId ? 'Certificate updated' : 'Certificate added');
      }
      refresh();
      close();
    },
    onError: (error) => toast.error(errorMessage(error, 'Could not save the certificate')),
  });

  const remove = useMutation({
    mutationFn: (id) => studentApi.deleteCertification(id),
    onSuccess: () => {
      toast.success('Certificate removed');
      refresh();
      setDeleting(null);
    },
    onError: (error) => toast.error(errorMessage(error, 'Could not remove the certificate')),
  });

  function submit() {
    // Blank boxes are sent as null rather than "": the backend validates the
    // dates, and an empty string is not one.
    save.mutate({
      input: Object.fromEntries(
        Object.entries(form).map(([key, value]) => [key, value.trim() || null]),
      ),
      attachment: file,
    });
  }

  function chooseFile(event) {
    const chosen = event.target.files?.[0];
    event.target.value = '';
    if (!chosen) return;
    if (chosen.size > CERTIFICATE_MAX_SIZE) {
      toast.error('File is too large. Maximum 5 MB.');
      return;
    }
    setFile(chosen);
  }

  function startAdd() {
    setForm(BLANK);
    setEditingId(null);
    setFile(null);
    setOpen(true);
  }

  function startEdit(certificate) {
    setForm(toForm(certificate));
    setEditingId(certificate.id);
    setFile(null);
    setOpen(true);
  }

  function field(name) {
    return {
      value: form[name],
      onChange: (event) => setForm({ ...form, [name]: event.target.value }),
    };
  }

  const formPanel = (
    <div className="border-line rounded-input mb-4 space-y-4 border p-4">
      <Input
        label="Name"
        required
        placeholder="AWS Certified Cloud Practitioner"
        {...field('name')}
      />
      <Input label="Issued by" placeholder="Amazon Web Services" {...field('issuer')} />
      <div className="grid gap-4 sm:grid-cols-2">
        <Input label="Issued" type="date" {...field('issue_date')} />
        <Input label="Expires" type="date" {...field('expiry_date')} />
      </div>
      <Input label="Credential ID" {...field('credential_id')} />

      <div>
        <p className="text-ink text-sm font-medium">Certificate file</p>
        <input
          ref={fileInput}
          type="file"
          accept={CERTIFICATE_ACCEPT}
          className="sr-only"
          tabIndex={-1}
          aria-hidden="true"
          onChange={chooseFile}
        />
        <div className="mt-1.5 flex flex-wrap items-center gap-2">
          <Button variant="secondary" size="sm" onClick={() => fileInput.current?.click()}>
            <Icon name="upload" size={14} />
            {file || editing?.file_name ? 'Choose another file' : 'Upload certificate'}
          </Button>
          {file ? (
            <span className="text-ink inline-flex items-center gap-1.5 text-sm">
              <Icon name="fileText" size={14} />
              {file.name} · {formatFileSize(file.size)}
              <button
                type="button"
                className="text-ink-muted hover:text-danger-700 ml-1"
                aria-label="Do not upload this file"
                onClick={() => setFile(null)}
              >
                <Icon name="x" size={14} />
              </button>
            </span>
          ) : editing?.file_name ? (
            <span className="text-ink-muted inline-flex items-center gap-2 text-sm">
              {editing.file_name}
              <Badge dot tone={DOCUMENT_TONES[editing.verification_status] ?? 'neutral'}>
                {STUDENT_CERTIFICATE_LABELS[editing.verification_status] ??
                  editing.verification_status}
              </Badge>
            </span>
          ) : (
            <span className="text-ink-muted text-xs">{CERTIFICATE_HINT}</span>
          )}
        </div>
        <p className="text-ink-muted mt-1.5 text-xs">
          Uploading sends it to an admin for review. Changing the name, issuer, dates or credential
          ID of a reviewed certificate sends it back for review.
        </p>
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="secondary" size="sm" onClick={close}>
          Cancel
        </Button>
        <Button
          size="sm"
          isLoading={save.isPending}
          disabled={form.name.trim().length < 2}
          onClick={submit}
        >
          {editingId ? 'Save certificate' : 'Add certificate'}
        </Button>
      </div>
    </div>
  );

  return (
    <Card>
      <CardHeader
        title={`Certificates (${certifications.length})`}
        description="Upload each certificate. An admin checks it before it shows as verified."
        action={
          !open && (
            <Button size="sm" onClick={startAdd}>
              <Icon name="plus" size={15} />
              Add certificate
            </Button>
          )
        }
      />

      {/* Adding opens the form at the top; editing opens it in place of the
          certificate being edited, where the student clicked. */}
      {open && !editingId && formPanel}

      {isPending ? (
        <p className="text-ink-muted text-sm">Loading…</p>
      ) : certifications.length === 0 ? (
        <EmptyState
          title="No certificates yet"
          description="A course certificate counts. So does a driving licence, if the role asks for one."
        />
      ) : (
        <>
          <Alert tone="info" className="mb-3">
            Only certificates an admin has checked show as verified to companies and on your public
            portfolio.
          </Alert>
          <ul className="space-y-3">
            {certifications.map((certificate) =>
              open && editingId === certificate.id ? (
                <li key={certificate.id} ref={editRow} className="scroll-mt-24">
                  {formPanel}
                </li>
              ) : (
                <li key={certificate.id} className="border-line rounded-input border p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 flex-1 basis-48">
                      <p className="text-ink font-medium">{certificate.name}</p>
                      {certificate.issuer && (
                        <p className="text-ink-muted text-sm">{certificate.issuer}</p>
                      )}
                      <p className="text-ink-muted mt-1 text-xs">
                        {certificate.issue_date
                          ? `Issued ${formatDate(certificate.issue_date)}`
                          : 'No issue date'}
                        {certificate.expiry_date &&
                          ` · expires ${formatDate(certificate.expiry_date)}`}
                        {certificate.credential_id && ` · ${certificate.credential_id}`}
                      </p>
                    </div>
                    <div className="flex shrink-0 flex-wrap items-center gap-1.5">
                      <Button
                        variant="secondary"
                        size="sm"
                        title={`Edit ${certificate.name}`}
                        onClick={() => startEdit(certificate)}
                      >
                        <Icon name="pencil" size={14} />
                        Edit
                      </Button>
                      <Button
                        variant="ghostDanger"
                        size="sm"
                        title={`Remove ${certificate.name}`}
                        onClick={() => setDeleting(certificate)}
                      >
                        <Icon name="trash" size={14} />
                        Remove
                      </Button>
                    </div>
                  </div>
                  <div className="mt-3">
                    <CertificatePanel certificate={certificate} onChanged={refresh} />
                  </div>
                </li>
              ),
            )}
          </ul>
        </>
      )}

      <ConfirmDialog
        open={Boolean(deleting)}
        title="Remove this certificate?"
        message={
          deleting
            ? `"${deleting.name}" is removed from your profile${
                deleting.file_name ? ', with its uploaded file' : ''
              }.`
            : ''
        }
        confirmLabel="Remove"
        isLoading={remove.isPending}
        onConfirm={() => deleting && remove.mutate(deleting.id)}
        onCancel={() => setDeleting(null)}
      />
    </Card>
  );
}
